import { Suspense, lazy, useCallback, useMemo, useState } from 'react';

import { useAuthContext } from '@picsart/growth-rc/AuthContext';
import Loading, { LoadingMode } from '@picsart/rc/old/Loading';

import { ModalState } from 'components/Decorators/withAction/types';

import useDevice from 'hooks/useDeviceType';
import useAnalytics from 'services/Analytics';

import { AnalyticEventTypes, LandingCategory } from 'constants/enums';
import { getRedirectOnMobile } from 'constants/main';
import useLandingPropsStore from 'stores/landingPropsStore';
import useMonetizationDataStore from 'stores/monetizationDataStore';
import { removeStorage } from 'utils/crudStorage';
import { ActionButtonProps } from 'components/Buttons/ActionButton/types';
import usePageDataStore from 'stores/pageDataStore';
import usePageSlug from 'hooks/usePageSlug';
import useSubscriptionStore from 'stores/useSubscriptionStore';

const AuthModal = lazy(() => import(/* webpackChunkName: "AuthModal" */ 'components/AuthModal'));

// TODO : needs to do refactoring ASAP !!!(very confusing logic)
function withAction(Component) {
  return function HOC(props: ActionButtonProps) {
    const { qrModal, url, authorizationRequired, ignorePremiumPopupPackage } = props;

    const [modalsState, setModalsState] = useState<ModalState>({
      isAuthModalOpen: false,
      isLoading: false,
      authGtmData: {},
      url: '',
    });
    const setPremiumPopup = useMonetizationDataStore(x => x.monetizationPopupTrigger);
    const premiumPopupPackage = useLandingPropsStore(x => x.premiumPopupPackage);
    const screenName = useLandingPropsStore(x => x.screenName);
    const needToOpenRegModal = usePageDataStore(x => x.needToOpenRegModal);
    const category = useLandingPropsStore(x => x.category);
    const { setRedirectTo } = useSubscriptionStore(store => ({
      setRedirectTo: store.setRedirectTo,
    }));

    // NOTE! dont move to <modalsState>. Need with this custom simple awai, for rendering Monetization modal

    const { fireEvent } = useAnalytics();
    const { user } = useAuthContext();
    const { isMobile } = useDevice();

    const { slug: pagePath } = usePageSlug();
    const isHomePage = `/${pagePath}` === '/';
    const regFlowExperiment = needToOpenRegModal && isHomePage;

    const isUserTemporary = user?.account_type === 'temporary';

    const openAuth = useMemo(
      () =>
        !user &&
        ((!qrModal &&
          (!premiumPopupPackage || ignorePremiumPopupPackage) &&
          authorizationRequired) ||
          (isMobile && premiumPopupPackage && !ignorePremiumPopupPackage)),
      [
        user,
        qrModal,
        premiumPopupPackage,
        authorizationRequired,
        ignorePremiumPopupPackage,
        isMobile,
      ],
    );

    // Auth functions
    const closeAuthModal = useCallback(() => {
      if (!user) {
        fireEvent(AnalyticEventTypes.registration_done, {
          registration_type: 'standard',
          action: 'skip',
        });
        removeStorage({ name: 'skip_redirect', isTemp: true });
      }
      setModalsState(prevState => ({ ...prevState, isAuthModalOpen: false }));
    }, [fireEvent, user]);

    const goToNextUrl = useCallback(() => {
      if (category === LandingCategory.maximizer) {
        setRedirectTo(url);
        return;
      }
      if (regFlowExperiment) {
        setTimeout(() => {
          window.location.href = '/create';
        }, 100);
      } else {
        setModalsState(prevState => ({ ...prevState, isAuthModalOpen: false }));

        if ((!isMobile && modalsState.url) || getRedirectOnMobile(screenName)) {
          window.location.href = modalsState.url;
          return;
        }

        window.location.href = url;
      }
    }, [category, isMobile, modalsState?.url, regFlowExperiment, screenName, setRedirectTo, url]);

    return (
      <>
        <Component
          setModalsState={setModalsState}
          {...props}
          setPremiumPopup={setPremiumPopup}
          isUserTemporary={isUserTemporary}
          openAuth={openAuth}
        />
        {modalsState.isAuthModalOpen && (
          <Suspense fallback={<Loading />}>
            <AuthModal
              isOpen={modalsState.isAuthModalOpen}
              gtmData={modalsState.authGtmData}
              closeHandler={closeAuthModal}
              successHandler={goToNextUrl}
            />
          </Suspense>
        )}

        {modalsState.isLoading && <Loading mode={LoadingMode.FullScreen} />}
      </>
    );
  };
}

export default withAction;
